import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { LoadingSpinner } from "@/components/brand/LoadingSpinner"
import { Mail, Calendar, TrendingUp } from "lucide-react"
import { useClientMailboxSentAnalytics, ClientMailboxSentFilter } from "@/hooks/useClientMailboxSentAnalytics"

interface ClientMailboxSentModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  filter?: ClientMailboxSentFilter
}

export function ClientMailboxSentModal({ open, onOpenChange, filter }: ClientMailboxSentModalProps) {
  const { data: mailboxes = [], isLoading } = useClientMailboxSentAnalytics(filter)

  const getSentCountForPeriod = (mailbox: any, period: string) => {
    switch (period) {
      case 'today':
        return mailbox.sent_today
      case 'yesterday':
        return mailbox.sent_7d - mailbox.sent_today
      case 'last_7_days':
        return mailbox.sent_7d
      case 'last_14_days':
        return mailbox.sent_14d
      case 'last_30_days':
        return mailbox.total_sent
      default:
        return mailbox.sent_today
    }
  }

  const getPeriodDisplayName = (period: string) => {
    switch (period) {
      case 'today':
        return 'Today'
      case 'yesterday':
        return 'Yesterday'
      case 'last_7_days':
        return 'Last 7 Days'
      case 'last_14_days':
        return 'Last 14 Days'
      case 'last_30_days':
        return 'Last 30 Days'
      default:
        return 'Today'
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh]">
        <DialogHeader className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold">
                Mailbox Email Sent Details
              </DialogTitle>
              {filter && (
                <p className="text-sm text-muted-foreground">
                  {filter.clientName} • {getPeriodDisplayName(filter.timePeriod)}
                </p>
              )}
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : mailboxes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Mail className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="font-medium text-muted-foreground mb-2">No emails sent</h3>
              <p className="text-sm text-muted-foreground">
                No mailboxes found with sent emails for the selected criteria
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-card rounded-lg p-4 border">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Total Mailboxes</span>
                  </div>
                  <div className="text-2xl font-bold text-primary">
                    {mailboxes.length}
                  </div>
                </div>
                
                <div className="bg-card rounded-lg p-4 border">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Total Sent</span>
                  </div>
                  <div className="text-2xl font-bold text-primary">
                    {mailboxes.reduce((sum, m) => sum + m.total_sent, 0).toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold">Mailbox</TableHead>
                      <TableHead className="text-center font-semibold">Today</TableHead>
                      <TableHead className="text-center font-semibold">Last 7 Days</TableHead>
                      <TableHead className="text-center font-semibold">Last 14 Days</TableHead>
                      <TableHead className="text-center font-semibold">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mailboxes.map((mailbox) => (
                      <TableRow key={mailbox.email} className="hover:bg-muted/30">
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium">{mailbox.email}</div>
                            <div className="text-xs text-muted-foreground">
                              {mailbox.client_name}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {mailbox.sent_today > 0 ? (
                            <span className="font-medium">{mailbox.sent_today.toLocaleString()}</span>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {mailbox.sent_7d > 0 ? (
                            <span className="font-medium">{mailbox.sent_7d.toLocaleString()}</span>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {mailbox.sent_14d > 0 ? (
                            <span className="font-medium">{mailbox.sent_14d.toLocaleString()}</span>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-semibold text-primary">
                            {mailbox.total_sent.toLocaleString()}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}